import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import Divider from 'antd/es/divider';

import featureToggles from 'config/featureToggles';

import useQueryParam from 'hooks/useQueryParam';
import { useFeatureToggleItems } from 'components/FeatureToggle';
import usePageTitle from 'hooks/usePageTitle';

import MonitorLayout from 'layouts/Monitor.layout';
import { Text } from 'components/Text';

import FullscreenSwitcher from '../components/FullscreenSwitcherButton';
import Clock from '../components/Clock';

import { monitorSwitchingViews, monitorSwitchingViewsDuration } from '../constants';

import DoorsMonitor from '../containers/DoorsMonitor';
import DoorsMonitor2 from '../containers/DoorMonitor2';
import TopEmplotyeesMonitor from '../containers/TopEmployeesMonitor';

const monitorViewVariants = {
  [monitorSwitchingViews.DOORS]: {
    '': DoorsMonitor,
    'v2': DoorsMonitor2,
  },
  [monitorSwitchingViews.EMPLOYEES]: {
    '': TopEmplotyeesMonitor,
  }
};

const Monitor = () => {
  const [monitorInfo, setMonitorInfo] = useState({ locationName: '', tzOffset: null });
  const [hardwareId] = useQueryParam('hw_id');
  const [workGroup] = useQueryParam('work_group');
  const [zoneName] = useQueryParam('zone_name');
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [switchingViews, setSwitchingViews] = useState([]);

  const monitorFeatureToggles = useFeatureToggleItems(featureToggles.BIG_MONITOR);

  useEffect(() => {
    const switchingViews = [];
    Object.values(monitorSwitchingViews).forEach((view) => {
      const viewToggle = monitorFeatureToggles.find(toggle => toggle.itemName === view);

      const isVisible = viewToggle?.visible !== undefined ? viewToggle.visible : true;
      const variant = viewToggle?.variant !== undefined ? viewToggle.variant : '';

      if (isVisible) {
        switchingViews.push({
          view,
          component: monitorViewVariants[view][variant],
        });
      }
    });

    setSwitchingViews(switchingViews);
    setActiveViewIndex(0);
  }, [monitorFeatureToggles]);

  useEffect(() => {
    const activeView = switchingViews.find((_, index) => index === activeViewIndex);

    const setViewTimer = (activeViewIndex) => {
      return setTimeout(() => {
        let newIndex = (activeViewIndex + 1) % switchingViews.length;
        setActiveViewIndex(newIndex);
      }, monitorSwitchingViewsDuration[activeView.view] || (60 * 1000));
    };

    if (switchingViews.length > 1) {
      const timer = setViewTimer(activeViewIndex);
      return () => {
        clearTimeout(timer);
      };

    } else if (switchingViews.length === 1 && activeViewIndex !== 0) {
      setActiveViewIndex(0);
    }
  }, [switchingViews, activeViewIndex]);

  const activeViewProps = {
    hardwareId,
    workGroup,
    zoneName,
    monitorInfo,
    setMonitorInfo,
  };

  const ActiveView = switchingViews[activeViewIndex]?.component;
  const pageTitle = 'Monitor';
  usePageTitle(pageTitle);

  return (
    <MonitorLayout
      title={monitorInfo.locationName ? (
        <>
          <StyledDivider type="vertical" orientation="center" />
          <DCName>{monitorInfo.locationName}</DCName>
        </>
      ) : null}
      extra={monitorInfo.tzOffset ? (
        <Clock format="pp" tzOffset={monitorInfo.tzOffset} />
      ) : null}
    >
      {ActiveView ? <ActiveView {...activeViewProps} /> : null}
      <FullscreenSwitcher style={{ position: 'fixed', bottom: '1.7vh', right: '1.7vh' }} />
    </MonitorLayout>
  );
};

const DCName = styled(Text)`
  color: #F0FDFF;
  font-weight: 600;
  padding-left: 12px;
`;

const StyledDivider = styled(Divider)`
  height: 4vh;
  margin: 0 1vw;
  border-left-color: #F0FDFF;
`;

export default Monitor;
